import { useState } from "react";
import { Bell, CheckCheck, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  useNotifications,
  useMarkAllAsRead,
  useClearAllNotifications,
} from "@/lib/tanstack-query/notifications";
import { useInfiniteScroll } from "@/hooks/use-infinite-scroll";
import { NotificationItem } from "./NotificationItem";

interface NotificationPopoverProps {
  onClose: () => void;
}

export function NotificationPopover({ onClose }: NotificationPopoverProps) {
  const [showClearDialog, setShowClearDialog] = useState(false);
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useNotifications();
  const markAllAsRead = useMarkAllAsRead();
  const clearAll = useClearAllNotifications();

  const sentinelRef = useInfiniteScroll({
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  });

  const notifications = data?.pages.flatMap((p) => p.results) ?? [];

  return (
    <>
      <div className="w-[calc(100vw-2rem)] sm:w-[380px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          {notifications.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => markAllAsRead.mutate()}
                disabled={markAllAsRead.isPending || notifications.length === 0}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Read all
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs"
                onClick={() => setShowClearDialog(true)}
                disabled={clearAll.isPending || notifications.length === 0}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Clear all
              </Button>
            </div>
          )}
        </div>

        {/* Body */}
        <ScrollArea className="h-[60vh] sm:h-[400px] max-h-[60vh]">
          {isLoading ? (
            <div className="space-y-1 p-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-8 w-8 rounded-full shrink-0" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState
              icon={Bell}
              title="No notifications"
              description="You're all caught up!"
              className="border-none"
            />
          ) : (
            <>
              {notifications.map((notification, i) => (
                <div key={notification.id}>
                  {i > 0 && <Separator />}
                  <NotificationItem
                    notification={notification}
                    onClose={onClose}
                  />
                </div>
              ))}
              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} />
              {isFetchingNextPage && (
                <div className="flex justify-center py-3">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </>
          )}
        </ScrollArea>
      </div>

      {/* Clear all confirmation */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear all notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove all notifications from your list. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                clearAll.mutate();
                setShowClearDialog(false);
              }}
            >
              Clear all
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
